import {
  Factory, Target, IndianRupee, Gauge, AlertTriangle, Clock,
  PackageCheck, TruckIcon, CheckCircle2, UsersRound,
  TicketIcon, Star, ShieldCheck, Banknote,
  Handshake, Receipt, Wallet, TrendingUp,
  Package, AlertCircle, Warehouse,
  BarChart3, Timer, CalendarClock, UserCheck, Percent, Layers,
  // New role-KPI icons
  Phone, PhoneCall, PhoneOutgoing, Users2, MapPin,
  LineChart as LineChartIcon, PieChart as PieChartIcon,
  ClipboardList, FileText, ShoppingCart, Flame, Hammer,
  Wrench, Battery, BadgeCheck, ListChecks, HardHat,
  Fuel, SplitSquareHorizontal, Activity, Trophy, Medal,
  TimerReset, TrendingDown, CreditCard, Scale,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ─── Widget Definition ───────────────────────────────────────────────────────

export type WidgetVariant = 'stat' | 'list' | 'bar' | 'line' | 'pie' | 'stack' | 'map';

export interface WidgetDefinition {
  id: string;
  label: string;
  department: string;
  category: string;
  size: 'sm' | 'md' | 'lg';
  defaultLayout: { w: number; h: number };
  icon: LucideIcon;
  color: string;
  iconColor: string;
  format: 'number' | 'currency' | 'percentage' | 'text' | 'days';
  /** Legacy — only kept for the `charts.monthly_revenue` tile. New widgets use `variant`. */
  chartType?: 'bar' | null;
  /**
   * Backend endpoint the widget fetches from. If set, the widget self-fetches
   * via RoleKpiWidget. If unset, the widget pulls its value from the shared
   * /analytics/owner-dashboard payload keyed by widget `id` (legacy behaviour).
   */
  endpoint?: string;
  /** Render mode used by RoleKpiWidget. */
  variant?: WidgetVariant;
}

// ─── Size → grid units mapping ───────────────────────────────────────────────

export const SIZE_MAP = {
  sm: { w: 1, h: 1 },
  md: { w: 2, h: 1 },
  lg: { w: 4, h: 2 },
};

const LG22 = { w: 2, h: 2 }; // half-row chart tile
const LG42 = { w: 4, h: 2 }; // full-row chart tile
const MD31 = { w: 3, h: 2 }; // 3-col span, 2 rows

// ─── Categories for selector panel ──────────────────────────────────────────

export const WIDGET_CATEGORIES = [
  { id: 'owner', label: 'Owner KPIs', color: 'bg-indigo-500' },
  { id: 'sales', label: 'Sales', color: 'bg-blue-500' },
  { id: 'production', label: 'Production', color: 'bg-orange-500' },
  { id: 'service', label: 'Service', color: 'bg-green-500' },
  { id: 'inventory', label: 'Inventory', color: 'bg-purple-500' },
  { id: 'procurement', label: 'Procurement', color: 'bg-amber-500' },
  { id: 'finance', label: 'Finance', color: 'bg-emerald-500' },
  { id: 'my', label: 'My Desk', color: 'bg-rose-500' },
  { id: 'charts', label: 'Charts', color: 'bg-cyan-500' },
];

// ─── Widget Registry ─────────────────────────────────────────────────────────

export const WIDGET_REGISTRY: WidgetDefinition[] = [
  // ── Owner Cross-Department (7) — legacy, served from /owner-dashboard ──
  { id: 'owner.dso', label: 'Days Sales Outstanding', department: 'owner', category: 'owner', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: CalendarClock, color: 'bg-indigo-100', iconColor: 'text-indigo-600', format: 'days' },
  { id: 'owner.revenue_per_employee', label: 'Revenue / Employee', department: 'owner', category: 'owner', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: UserCheck, color: 'bg-indigo-100', iconColor: 'text-indigo-600', format: 'currency' },
  { id: 'owner.oee', label: 'OEE (Overall Equipment Effectiveness)', department: 'owner', category: 'owner', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Gauge, color: 'bg-indigo-100', iconColor: 'text-indigo-600', format: 'percentage' },
  { id: 'owner.order_to_delivery', label: 'Order → Delivery Lead Time', department: 'owner', category: 'owner', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Timer, color: 'bg-indigo-100', iconColor: 'text-indigo-600', format: 'days' },
  { id: 'owner.warranty_claim_rate', label: 'Warranty Claim Rate', department: 'owner', category: 'owner', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: ShieldCheck, color: 'bg-indigo-100', iconColor: 'text-indigo-600', format: 'percentage' },
  { id: 'owner.delivery_sla', label: 'Delivery SLA %', department: 'owner', category: 'owner', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TruckIcon, color: 'bg-indigo-100', iconColor: 'text-indigo-600', format: 'percentage' },
  { id: 'owner.product_mix_margin', label: 'Top Product Margin', department: 'owner', category: 'owner', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Percent, color: 'bg-indigo-100', iconColor: 'text-indigo-600', format: 'percentage' },

  // ── Sales — legacy KPIs from /owner-dashboard ──
  { id: 'sales.revenue_achieved', label: 'Revenue Collected', department: 'sales', category: 'sales', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: IndianRupee, color: 'bg-green-100', iconColor: 'text-green-600', format: 'currency' },
  { id: 'sales.pipeline_value', label: 'Pipeline Value', department: 'sales', category: 'sales', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Layers, color: 'bg-cyan-100', iconColor: 'text-cyan-600', format: 'currency' },
  { id: 'sales.active_deals', label: 'Active Deals', department: 'sales', category: 'sales', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Handshake, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number' },
  { id: 'sales.win_rate', label: 'Win Rate', department: 'sales', category: 'sales', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TrendingUp, color: 'bg-lime-100', iconColor: 'text-lime-600', format: 'percentage' },
  { id: 'sales.outstanding', label: 'Outstanding Receivables', department: 'sales', category: 'sales', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Wallet, color: 'bg-rose-100', iconColor: 'text-rose-600', format: 'currency' },

  // ── Sales — role-KPI endpoints ──
  { id: 'sales.top_deals_closing', label: 'Top Deals Closing', department: 'sales', category: 'sales', size: 'lg',
    defaultLayout: LG22, icon: Trophy, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/sales/top-deals-closing?limit=5', variant: 'list' },
  { id: 'sales.overdue_followups', label: 'Overdue Follow-ups', department: 'sales', category: 'sales', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: AlertCircle, color: 'bg-rose-100', iconColor: 'text-rose-600', format: 'number',
    endpoint: '/analytics/sales/overdue-followups' },
  { id: 'sales.deals_slipping', label: 'Deals Slipping', department: 'sales', category: 'sales', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TrendingDown, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'number',
    endpoint: '/analytics/sales/deals-slipping' },
  { id: 'sales.team_activities_today', label: 'Team Activities Today', department: 'sales', category: 'sales', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Activity, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/sales/team-activities-today' },
  { id: 'sales.quota_attainment_by_manager', label: 'Quota Attainment by Manager', department: 'sales', category: 'sales', size: 'lg',
    defaultLayout: LG22, icon: Target, color: 'bg-green-100', iconColor: 'text-green-600', format: 'currency',
    endpoint: '/analytics/sales/quota-attainment?groupBy=manager', variant: 'bar' },
  { id: 'sales.lead_source_mix', label: 'Lead Source Mix', department: 'sales', category: 'sales', size: 'lg',
    defaultLayout: LG22, icon: PieChartIcon, color: 'bg-cyan-100', iconColor: 'text-cyan-600', format: 'number',
    endpoint: '/analytics/sales/lead-source-mix', variant: 'pie' },
  { id: 'sales.revenue_forecast', label: 'Revenue Forecast (90d)', department: 'sales', category: 'sales', size: 'lg',
    defaultLayout: LG22, icon: LineChartIcon, color: 'bg-green-100', iconColor: 'text-green-600', format: 'currency',
    endpoint: '/analytics/sales/revenue-forecast', variant: 'line' },
  { id: 'sales.loss_reasons', label: 'Loss Reasons', department: 'sales', category: 'sales', size: 'lg',
    defaultLayout: LG22, icon: TrendingDown, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number',
    endpoint: '/analytics/sales/loss-reasons', variant: 'bar' },
  { id: 'sales.rep_leaderboard', label: 'Rep Leaderboard', department: 'sales', category: 'sales', size: 'lg',
    defaultLayout: LG22, icon: Medal, color: 'bg-yellow-100', iconColor: 'text-yellow-600', format: 'currency',
    endpoint: '/analytics/sales/rep-leaderboard', variant: 'bar' },
  { id: 'sales.stage_conversion', label: 'Stage Conversion', department: 'sales', category: 'sales', size: 'lg',
    defaultLayout: LG22, icon: SplitSquareHorizontal, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'percentage',
    endpoint: '/analytics/sales/stage-conversion', variant: 'bar' },

  // ── Production — legacy ──
  { id: 'production.daily_output', label: 'Daily Output', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Factory, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number' },
  { id: 'production.target_achievement', label: 'Target Achievement', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Target, color: 'bg-green-100', iconColor: 'text-green-600', format: 'percentage' },
  { id: 'production.defect_rate', label: 'Defect Rate', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: AlertTriangle, color: 'bg-red-100', iconColor: 'text-red-600', format: 'percentage' },
  { id: 'production.machine_utilization', label: 'Machine Utilization', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Gauge, color: 'bg-purple-100', iconColor: 'text-purple-600', format: 'percentage' },

  // ── Production — role-KPI endpoints ──
  { id: 'production.orders_at_risk', label: 'Orders at Risk', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: AlertTriangle, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number',
    endpoint: '/analytics/production/orders-at-risk' },
  { id: 'production.open_orders', label: 'Open Work Orders', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: ClipboardList, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/production/open-orders' },
  { id: 'production.rm_shortages', label: 'Raw Material Shortages', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Fuel, color: 'bg-orange-100', iconColor: 'text-orange-600', format: 'number',
    endpoint: '/analytics/production/rm-shortages' },
  { id: 'production.qc_pass_rate', label: 'QC Pass Rate', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: BadgeCheck, color: 'bg-green-100', iconColor: 'text-green-600', format: 'percentage',
    endpoint: '/analytics/production/qc-pass-rate' },
  { id: 'production.rejections_week', label: 'Rejections This Week', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: AlertCircle, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number',
    endpoint: '/analytics/production/rejections?period=week' },
  { id: 'production.shift_utilization', label: 'Shift Utilization', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Gauge, color: 'bg-purple-100', iconColor: 'text-purple-600', format: 'percentage',
    endpoint: '/analytics/production/shift-utilization' },
  { id: 'production.wip_value', label: 'WIP Value', department: 'production', category: 'production', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Hammer, color: 'bg-orange-100', iconColor: 'text-orange-600', format: 'currency',
    endpoint: '/analytics/production/wip' },
  { id: 'production.bottleneck_workcentres', label: 'Bottleneck Workcentres', department: 'production', category: 'production', size: 'lg',
    defaultLayout: LG42, icon: Activity, color: 'bg-orange-100', iconColor: 'text-orange-600', format: 'number',
    endpoint: '/analytics/production/bottleneck-workcentres?top=5', variant: 'bar' },

  // ── Service — legacy ──
  { id: 'service.open_tickets', label: 'Open Tickets', department: 'service', category: 'service', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TicketIcon, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number' },
  { id: 'service.csat_score', label: 'CSAT Score', department: 'service', category: 'service', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Star, color: 'bg-yellow-100', iconColor: 'text-yellow-600', format: 'text' },
  { id: 'service.warranty_claims', label: 'Warranty Claims', department: 'service', category: 'service', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: ShieldCheck, color: 'bg-purple-100', iconColor: 'text-purple-600', format: 'number' },

  // ── Service — role-KPI endpoints ──
  { id: 'service.sla_breach_count', label: 'SLA Breaches (Open)', department: 'service', category: 'service', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TimerReset, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number',
    endpoint: '/analytics/service/sla-breach-count' },
  { id: 'service.sla_breach_rate', label: 'SLA Breach Rate (30d)', department: 'service', category: 'service', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TimerReset, color: 'bg-red-100', iconColor: 'text-red-600', format: 'percentage',
    endpoint: '/analytics/service/sla-breach' },
  { id: 'service.visits_today', label: 'Visits Scheduled Today', department: 'service', category: 'service', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: MapPin, color: 'bg-green-100', iconColor: 'text-green-600', format: 'number',
    endpoint: '/analytics/service/visits-today' },
  { id: 'service.mttr', label: 'MTTR (hours, 30d)', department: 'service', category: 'service', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Timer, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/service/mttr' },
  { id: 'service.technician_utilization', label: 'Technician Utilization', department: 'service', category: 'service', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: HardHat, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'percentage',
    endpoint: '/analytics/service/technician-utilization' },
  { id: 'service.installation_completion', label: 'Installation Completion %', department: 'service', category: 'service', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: CheckCircle2, color: 'bg-green-100', iconColor: 'text-green-600', format: 'percentage',
    endpoint: '/analytics/service/installation-completion' },
  { id: 'service.tickets_per_technician', label: 'Tickets per Technician', department: 'service', category: 'service', size: 'lg',
    defaultLayout: LG42, icon: UsersRound, color: 'bg-green-100', iconColor: 'text-green-600', format: 'number',
    endpoint: '/analytics/service/tickets-per-technician', variant: 'bar' },

  // ── Inventory — legacy ──
  { id: 'inventory.total_skus', label: 'Total SKUs', department: 'inventory', category: 'inventory', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Package, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number' },
  { id: 'inventory.below_min_stock', label: 'Below Min Stock', department: 'inventory', category: 'inventory', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: AlertCircle, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number' },
  { id: 'inventory.total_value', label: 'Inventory Value', department: 'inventory', category: 'inventory', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Warehouse, color: 'bg-green-100', iconColor: 'text-green-600', format: 'currency' },

  // ── Inventory — role-KPI endpoints ──
  { id: 'inventory.stockouts_count', label: 'Stock-outs', department: 'inventory', category: 'inventory', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: AlertTriangle, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number',
    endpoint: '/analytics/inventory/stockouts' },
  { id: 'inventory.pending_grns', label: 'Pending GRNs', department: 'inventory', category: 'inventory', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: PackageCheck, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'number',
    endpoint: '/analytics/inventory/pending-grns' },
  { id: 'inventory.movements_today', label: 'Stock Movements Today', department: 'inventory', category: 'inventory', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Activity, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/inventory/movements-today' },
  { id: 'inventory.turns', label: 'Inventory Turns (yr)', department: 'inventory', category: 'inventory', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Gauge, color: 'bg-cyan-100', iconColor: 'text-cyan-600', format: 'number',
    endpoint: '/analytics/inventory/turns' },
  { id: 'inventory.accuracy_pct', label: 'Inventory Accuracy', department: 'inventory', category: 'inventory', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: BadgeCheck, color: 'bg-green-100', iconColor: 'text-green-600', format: 'percentage',
    endpoint: '/analytics/inventory/accuracy' },
  { id: 'inventory.days_of_inventory', label: 'Days of Inventory', department: 'inventory', category: 'inventory', size: 'lg',
    defaultLayout: LG22, icon: CalendarClock, color: 'bg-purple-100', iconColor: 'text-purple-600', format: 'days',
    endpoint: '/analytics/inventory/days-of-inventory?groupBy=category', variant: 'bar' },
  { id: 'inventory.slow_moving_value', label: 'Slow-Moving Stock Value', department: 'inventory', category: 'inventory', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Battery, color: 'bg-rose-100', iconColor: 'text-rose-600', format: 'currency',
    endpoint: '/analytics/inventory/slow-moving' },
  { id: 'inventory.recent_dispatches', label: 'Recent Dispatches', department: 'inventory', category: 'inventory', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TruckIcon, color: 'bg-green-100', iconColor: 'text-green-600', format: 'number',
    endpoint: '/analytics/inventory/recent-dispatches?limit=5', variant: 'list' },

  // ── Procurement — role-KPI endpoints ──
  { id: 'procurement.open_pos', label: 'Open Purchase Orders', department: 'procurement', category: 'procurement', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: ShoppingCart, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'number',
    endpoint: '/analytics/procurement/open-pos' },
  { id: 'procurement.pending_grns', label: 'Vendor Pending GRN', department: 'procurement', category: 'procurement', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: PackageCheck, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'number',
    endpoint: '/analytics/procurement/pending-grns' },
  { id: 'procurement.vendor_overdue', label: 'Vendors Overdue', department: 'procurement', category: 'procurement', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: AlertCircle, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number',
    endpoint: '/analytics/procurement/vendor-overdue' },
  { id: 'procurement.rfqs_awaiting_award', label: 'RFQs Awaiting Award', department: 'procurement', category: 'procurement', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: FileText, color: 'bg-cyan-100', iconColor: 'text-cyan-600', format: 'number',
    endpoint: '/analytics/procurement/rfqs-awaiting-award' },
  { id: 'procurement.avg_cycle_days', label: 'Avg Procurement Cycle', department: 'procurement', category: 'procurement', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Timer, color: 'bg-cyan-100', iconColor: 'text-cyan-600', format: 'days',
    endpoint: '/analytics/procurement/avg-cycle-days' },
  { id: 'procurement.spend_mtd', label: 'Spend MTD', department: 'procurement', category: 'procurement', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: IndianRupee, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'currency',
    endpoint: '/analytics/procurement/spend-mtd' },
  { id: 'procurement.price_change_alerts', label: 'Price Change Alerts (14d)', department: 'procurement', category: 'procurement', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TrendingUp, color: 'bg-rose-100', iconColor: 'text-rose-600', format: 'number',
    endpoint: '/analytics/procurement/price-change-alerts' },

  // ── Finance — legacy ──
  { id: 'finance.revenue_invoiced', label: 'Revenue Invoiced', department: 'finance', category: 'finance', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Receipt, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'currency' },
  { id: 'finance.invoices_overdue', label: 'Overdue Invoices', department: 'finance', category: 'finance', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Clock, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number' },

  // ── Finance — role-KPI endpoints ──
  { id: 'finance.cash_position', label: 'Cash Position (MTD)', department: 'finance', category: 'finance', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Banknote, color: 'bg-emerald-100', iconColor: 'text-emerald-600', format: 'currency',
    endpoint: '/analytics/finance/cash-position' },
  { id: 'finance.expense_approval_queue', label: 'Expense Approvals Pending', department: 'finance', category: 'finance', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: ListChecks, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'number',
    endpoint: '/analytics/finance/expense-approval-queue' },
  { id: 'finance.bank_recon_pending', label: 'Bank Recon Pending', department: 'finance', category: 'finance', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Scale, color: 'bg-cyan-100', iconColor: 'text-cyan-600', format: 'number',
    endpoint: '/analytics/finance/bank-recon-pending' },
  { id: 'finance.gst_filing_status', label: 'GST Returns Overdue', department: 'finance', category: 'finance', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: ShieldCheck, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number',
    endpoint: '/analytics/finance/gst-filing-status' },
  { id: 'finance.pnl_mtd', label: 'P&L MTD', department: 'finance', category: 'finance', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TrendingUp, color: 'bg-emerald-100', iconColor: 'text-emerald-600', format: 'currency',
    endpoint: '/analytics/finance/pnl-mtd' },
  { id: 'finance.receivables_aging', label: 'Receivables Aging', department: 'finance', category: 'finance', size: 'lg',
    defaultLayout: LG22, icon: Clock, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'currency',
    endpoint: '/analytics/finance/receivables-aging', variant: 'stack' },
  { id: 'finance.top_debtors', label: 'Top Debtors', department: 'finance', category: 'finance', size: 'lg',
    defaultLayout: LG22, icon: Users2, color: 'bg-rose-100', iconColor: 'text-rose-600', format: 'currency',
    endpoint: '/analytics/finance/top-debtors?limit=10', variant: 'bar' },

  // ── MY (my-productivity extensions — per-user) ──
  // Carry-overs: legacy my.* keys live in analytics/my-productivity GET /my-productivity aggregate.
  { id: 'my.deals_open', label: 'My Open Deals', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Handshake, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number' },
  { id: 'my.deals_won', label: 'My Won Deals', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TrendingUp, color: 'bg-green-100', iconColor: 'text-green-600', format: 'number' },
  { id: 'my.pipeline_value', label: 'My Pipeline', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Layers, color: 'bg-cyan-100', iconColor: 'text-cyan-600', format: 'currency' },
  { id: 'my.win_rate', label: 'My Win Rate', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TrendingUp, color: 'bg-lime-100', iconColor: 'text-lime-600', format: 'percentage' },
  { id: 'my.tasks_in_progress', label: 'My Tasks In-Progress', department: 'owner', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: ClipboardList, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number' },
  { id: 'my.activities_this_month', label: 'My Activities (MTD)', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Activity, color: 'bg-indigo-100', iconColor: 'text-indigo-600', format: 'number' },
  { id: 'my.proposals', label: 'My Proposals', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: FileText, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'number' },
  { id: 'my.tickets_assigned', label: 'My Tickets Assigned', department: 'service', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: TicketIcon, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number' },
  { id: 'my.tickets_resolved', label: 'My Tickets Resolved', department: 'service', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: CheckCircle2, color: 'bg-green-100', iconColor: 'text-green-600', format: 'number' },
  { id: 'my.ticket_resolution', label: 'My Resolution %', department: 'service', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: BadgeCheck, color: 'bg-green-100', iconColor: 'text-green-600', format: 'percentage' },

  // MY — new endpoints
  { id: 'my.calls_today', label: 'Calls Today', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Phone, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/my-productivity/calls-today' },
  { id: 'my.calls_target', label: 'Calls Target', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Target, color: 'bg-green-100', iconColor: 'text-green-600', format: 'number',
    endpoint: '/analytics/my-productivity/calls-target' },
  { id: 'my.connect_rate', label: 'Connect Rate', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: PhoneCall, color: 'bg-lime-100', iconColor: 'text-lime-600', format: 'percentage',
    endpoint: '/analytics/my-productivity/connect-rate' },
  { id: 'my.high_priority_leads_left', label: 'High-Priority Leads Left', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Flame, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number',
    endpoint: '/analytics/my-productivity/high-priority-leads-left' },
  { id: 'my.meeting_conversion', label: 'Meeting Conversion', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Trophy, color: 'bg-yellow-100', iconColor: 'text-yellow-600', format: 'percentage',
    endpoint: '/analytics/my-productivity/meeting-conversion' },
  { id: 'my.call_log_last_hour', label: 'Recent Calls (1h)', department: 'sales', category: 'my', size: 'lg',
    defaultLayout: MD31, icon: PhoneOutgoing, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/my-productivity/call-log?window=1h', variant: 'list' },
  { id: 'my.next_followups', label: 'Next Follow-ups', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: CalendarClock, color: 'bg-indigo-100', iconColor: 'text-indigo-600', format: 'number',
    endpoint: '/analytics/my-productivity/next-followups?limit=5', variant: 'list' },
  { id: 'my.proposals_pending', label: 'My Proposals Pending', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: FileText, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'number',
    endpoint: '/analytics/my-productivity/proposals-pending' },
  { id: 'my.leads_today', label: 'My New Leads Today', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Users2, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/my-productivity/leads-today' },
  { id: 'my.quotes_pending', label: 'My Quotes Pending', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: FileText, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'number',
    endpoint: '/analytics/my-productivity/quotes-pending' },
  { id: 'my.geo_checkins_map', label: 'My Check-ins (7d)', department: 'sales', category: 'my', size: 'lg',
    defaultLayout: LG22, icon: MapPin, color: 'bg-green-100', iconColor: 'text-green-600', format: 'number',
    endpoint: '/analytics/my-productivity/geo-checkins?days=7', variant: 'map' },
  { id: 'my.outstanding_on_my_accounts', label: 'Outstanding (My Accounts)', department: 'sales', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Wallet, color: 'bg-rose-100', iconColor: 'text-rose-600', format: 'currency',
    endpoint: '/analytics/my-productivity/outstanding-my-accounts' },
  { id: 'my.shift_output_today', label: 'My Shift Output', department: 'production', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Factory, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/my-productivity/shift-output-today' },
  { id: 'my.line_efficiency', label: 'My Line Efficiency', department: 'production', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Gauge, color: 'bg-purple-100', iconColor: 'text-purple-600', format: 'percentage',
    endpoint: '/analytics/my-productivity/line-efficiency' },
  { id: 'my.rejections_today', label: 'My Rejections Today', department: 'production', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: AlertTriangle, color: 'bg-red-100', iconColor: 'text-red-600', format: 'number',
    endpoint: '/analytics/my-productivity/rejections-today' },
  { id: 'my.team_attendance', label: 'My Team Attendance', department: 'production', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: UsersRound, color: 'bg-green-100', iconColor: 'text-green-600', format: 'percentage',
    endpoint: '/analytics/my-productivity/team-attendance' },
  { id: 'my.workorders_queued', label: 'My Work-order Queue', department: 'production', category: 'my', size: 'lg',
    defaultLayout: LG22, icon: ClipboardList, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/my-productivity/workorders-queued?limit=5', variant: 'list' },
  { id: 'my.material_ready_check', label: 'Material Ready %', department: 'production', category: 'my', size: 'lg',
    defaultLayout: LG22, icon: PackageCheck, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'percentage',
    endpoint: '/analytics/my-productivity/material-ready', variant: 'bar' },
  { id: 'my.completed_today', label: 'My Completed Today', department: 'service', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: CheckCircle2, color: 'bg-green-100', iconColor: 'text-green-600', format: 'number',
    endpoint: '/analytics/my-productivity/completed-today' },
  { id: 'my.next_site_visits', label: 'Next Site Visits', department: 'service', category: 'my', size: 'lg',
    defaultLayout: LG22, icon: MapPin, color: 'bg-green-100', iconColor: 'text-green-600', format: 'number',
    endpoint: '/analytics/my-productivity/next-site-visits?limit=3', variant: 'list' },
  { id: 'my.parts_in_van', label: 'Parts in Van', department: 'service', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Wrench, color: 'bg-cyan-100', iconColor: 'text-cyan-600', format: 'number',
    endpoint: '/analytics/my-productivity/parts-in-van' },
  { id: 'my.mttr', label: 'My MTTR (hrs)', department: 'service', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: Timer, color: 'bg-blue-100', iconColor: 'text-blue-600', format: 'number',
    endpoint: '/analytics/my-productivity/mttr' },
  { id: 'my.pending_otp_verifications', label: 'Pending OTP Verifications', department: 'service', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: ShieldCheck, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'number',
    endpoint: '/analytics/my-productivity/pending-otp-verifications' },
  { id: 'my.installations_assigned', label: 'Installations Assigned', department: 'service', category: 'my', size: 'sm',
    defaultLayout: SIZE_MAP.sm, icon: HardHat, color: 'bg-amber-100', iconColor: 'text-amber-600', format: 'number',
    endpoint: '/analytics/my-productivity/installations-assigned' },

  // ── Charts (1) — legacy ──
  { id: 'charts.monthly_revenue', label: 'Monthly Revenue Trend', department: 'finance', category: 'charts', size: 'lg',
    defaultLayout: SIZE_MAP.lg, icon: BarChart3, color: 'bg-cyan-100', iconColor: 'text-cyan-600', format: 'currency', chartType: 'bar' },
];

export const WIDGET_MAP = Object.fromEntries(WIDGET_REGISTRY.map((w) => [w.id, w]));

// Default enabled — same set that used to default; only legacy widgets by default.
// Role layouts (roleLayouts.ts) override this per-role.
export const DEFAULT_ENABLED = WIDGET_REGISTRY
  .filter((w) => !w.endpoint) // exclude self-fetching widgets from default to keep landing lean
  .map((w) => w.id);

// Department → allowed widget departments mapping (legacy admin selector)
const DEPT_WIDGET_MAP: Record<string, string[]> = {
  sales:       ['sales', 'finance', 'charts', 'my'],
  production:  ['production', 'inventory', 'charts', 'my'],
  service:     ['service', 'charts', 'my'],
  inventory:   ['inventory', 'procurement', 'charts'],
  procurement: ['procurement', 'inventory', 'charts'],
  finance:     ['finance', 'charts'],
  puf:         ['production', 'inventory', 'charts'],
  packaging:   ['production', 'inventory', 'charts'],
};

/** Returns widget IDs visible to a user based on their role and department. */
export function getWidgetsForUser(
  roleName: string | undefined,
  departmentName: string | undefined | null,
): string[] {
  if (roleName === 'admin' || roleName === 'owner') return [...DEFAULT_ENABLED];

  const deptKey = (departmentName ?? '').toLowerCase().trim();
  const allowed = DEPT_WIDGET_MAP[deptKey];
  if (!allowed) return [...DEFAULT_ENABLED];

  return WIDGET_REGISTRY
    .filter((w) => allowed.includes(w.department) || allowed.includes(w.category))
    .map((w) => w.id);
}

// Generate default grid layout for given widget IDs
export function generateDefaultLayouts(enabledIds: string[]) {
  const cols = { lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 };
  const layouts: Record<string, Array<{ i: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number }>> = {};

  for (const bp of Object.keys(cols) as Array<keyof typeof cols>) {
    const c = cols[bp];
    let x = 0, y = 0, rowH = 0;
    layouts[bp] = enabledIds.map((id) => {
      const w = WIDGET_MAP[id];
      if (!w) return { i: id, x: 0, y: 0, w: 1, h: 1 };
      const gw = Math.min(w.defaultLayout.w, c);
      const gh = w.defaultLayout.h;
      if (x + gw > c) { x = 0; y += rowH; rowH = 0; }
      const item = { i: id, x, y, w: gw, h: gh, minW: 1, minH: 1 };
      x += gw;
      rowH = Math.max(rowH, gh);
      return item;
    });
  }

  return layouts;
}
